import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { DEFAULT_SITE_KEYWORDS, DEFAULT_SITE_META_DESCRIPTION } from "../seoConstants";
import "../styles/recipeDetail.css";

const SITE_ORIGIN = "https://slatkobezglutena.com";
const DEFAULT_DOC_TITLE = "Slatko bez glutena - Bezglutenski recepti s okusom doma";

export default function RecipeDetail() {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorScreen, setErrorScreen] = useState(false);
  const [shareHint, setShareHint] = useState(null);

  useEffect(() => {
    async function load() {
      const { data, error } = await supabase
        .from("recipes")
        .select("*, recipe_images(*)")
        .eq("id", id)
        .single();

      if (error || !data) {
        console.error("Greška pri učitavanju recepta:", error);
        setErrorScreen(true);
        setLoading(false);
        return;
      }

      // Sortiraj slike po redoslijedu ako postoji order polje, inače po id
      if (data.recipe_images && Array.isArray(data.recipe_images) && data.recipe_images.length > 0) {
        try {
          data.recipe_images.sort((a, b) => {
            // Provjeri da li postoji order polje
            if (a && b && 'order' in a && 'order' in b) {
              const orderA = a.order !== null && a.order !== undefined ? a.order : (a.id || 0);
              const orderB = b.order !== null && b.order !== undefined ? b.order : (b.id || 0);
              return orderA - orderB;
            }
            // Ako nema order polja, sortiraj po id
            return (a?.id || 0) - (b?.id || 0);
          });
        } catch (sortError) {
          console.warn("Greška pri sortiranju slika:", sortError);
          // Nastavi bez sortiranja ako ima greške
        }
      }

      setRecipe(data);
      setLoading(false);
    }

    load();
  }, [id]);

  // Kanonski i Open Graph URL u index.html bili su fiksni na početnu — neki preglednici pri
  // dijeljenju koriste te tagove umjesto stvarnog URL-a. Ovdje ih uskladimo s ovom stranicom.
  useEffect(() => {
    if (!recipe) return undefined;

    const pageUrl = `${window.location.origin}/recipes/${id}`;
    const shortDesc = (recipe.description || "").slice(0, 160);

    document.title = `${recipe.title} | Slatko bez glutena`;

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", pageUrl);

    const setOg = (prop, value) => {
      const el = document.querySelector(`meta[property="${prop}"]`);
      if (el) el.setAttribute("content", value);
    };
    setOg("og:url", pageUrl);
    setOg("og:title", recipe.title);
    if (shortDesc) {
      setOg("og:description", shortDesc);
    }

    const twUrl = document.querySelector('meta[name="twitter:url"]');
    if (twUrl) twUrl.setAttribute("content", pageUrl);
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute("content", recipe.title);

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && shortDesc) metaDesc.setAttribute("content", shortDesc);

    const extras = [recipe.category, recipe.taste, recipe.type]
      .filter((x) => x != null && String(x).trim())
      .map((x) => String(x).trim());
    const keywordsContent = extras.length
      ? `${DEFAULT_SITE_KEYWORDS}, ${extras.join(", ")}`
      : DEFAULT_SITE_KEYWORDS;
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute("content", keywordsContent);

    return () => {
      document.title = DEFAULT_DOC_TITLE;
      if (canonical) canonical.setAttribute("href", SITE_ORIGIN);
      setOg("og:url", SITE_ORIGIN);
      setOg("og:title", DEFAULT_DOC_TITLE);
      setOg("og:description", DEFAULT_SITE_META_DESCRIPTION);
      if (twUrl) twUrl.setAttribute("content", SITE_ORIGIN);
      if (twTitle) twTitle.setAttribute("content", DEFAULT_DOC_TITLE);
      if (metaDesc) {
        metaDesc.setAttribute("content", DEFAULT_SITE_META_DESCRIPTION);
      }
      if (metaKw) metaKw.setAttribute("content", DEFAULT_SITE_KEYWORDS);
    };
  }, [recipe, id]);

  async function handleShareRecipe() {
    const url = window.location.href;
    const title = recipe.title;
    const text = recipe.description || "";

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        setShareHint(null);
      } catch (err) {
        if (err && err.name === "AbortError") return;
        await copyRecipeLink(url);
      }
    } else {
      await copyRecipeLink(url);
    }
  }

  async function copyRecipeLink(url) {
    try {
      await navigator.clipboard.writeText(url);
      setShareHint("Link je kopiran.");
      setTimeout(() => setShareHint(null), 2500);
    } catch {
      window.prompt("Kopirajte link na recept:", url);
    }
  }

  if (loading)
    return <p className="loading">Učitavanje recepta…</p>;

  if (errorScreen)
    return (
      <div className="error-message">
        <h2>Ups! Nešto je pošlo po zlu</h2>
        <p>Nije moguće učitati recept. Molimo pokušajte ponovo.</p>
        <Link to="/" className="btn-back">← Vrati se na početnu</Link>
      </div>
    );

  if (!recipe) return null;

  // Priprema svih slika u poretku (već sortirane u useEffect)
  const allImages = recipe.recipe_images && recipe.recipe_images.length > 0
    ? recipe.recipe_images
    : [];

  return (
    <div className="recipe-detail">
      <div className="recipe-header">
        <h1>{recipe.title}</h1>
      </div>

      <p className="recipe-short">{recipe.description}</p>

      {/* Sve fotografije u poretku */}
      {allImages.length > 0 && (
        <div className="recipe-images">
          {allImages.map((img, index) => (
            <div key={img.id || index} className="recipe-image-wrapper">
              <img
                src={img.image_url}
                alt={recipe.title}
                className="recipe-image"
              />
            </div>
          ))}
        </div>
      )}

      <div className="recipe-body">
        <div>
          <h2>Sastojci</h2>
          <p className="ingredients-text">
            {recipe.ingredients ? (
              recipe.ingredients.split('\n')
                .filter(line => line.trim())
                .map((line, index, array) => (
                  <React.Fragment key={index}>
                    • {line.trim()}
                    {index < array.length - 1 && <br />}
                  </React.Fragment>
                ))
            ) : (
              recipe.ingredients
            )}
          </p>
        </div>

        <div>
          <h2>Priprema</h2>
          <p>{recipe.steps}</p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="recipe-actions">
        <button
          type="button"
          className="action-btn share-btn"
          onClick={handleShareRecipe}
          aria-label="Podijeli recept"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
          </svg>
          <span>Podijeli</span>
        </button>

        <button className="action-btn print-btn" onClick={() => window.print()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <path d="M6 14h12v8H6z" />
          </svg>
          <span>Print</span>
        </button>

        <button 
          className="action-btn pdf-btn" 
          onClick={() => {
            const printWindow = window.open('', '_blank');
            const formatIngredients = (ingredients) => {
              if (!ingredients) return '';
              return ingredients.split('\n')
                .filter(line => line.trim())
                .map(line => `• ${line.trim()}`)
                .join('\n');
            };
            
            const recipeContent = `
              <html>
                <head>
                  <title>${recipe.title}</title>
                  <style>
                    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                    h1 { color: #1f6074; font-size: 2rem; }
                    h2 { color: #1f6074; margin-top: 30px; }
                    p { line-height: 1.6; white-space: pre-line; }
                  </style>
                </head>
                <body>
                  <h1>${recipe.title}</h1>
                  ${recipe.description ? `<p>${recipe.description}</p>` : ''}
                  <h2>Sastojci</h2>
                  <p>${formatIngredients(recipe.ingredients)}</p>
                  <h2>Priprema</h2>
                  <p>${recipe.steps || ''}</p>
                </body>
              </html>
            `;
            printWindow.document.write(recipeContent);
            printWindow.document.close();
            printWindow.print();
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
            <path d="M10 9H8" />
          </svg>
          <span>PDF</span>
        </button>

        <a 
          className="action-btn email-btn" 
          href={`mailto:?subject=${encodeURIComponent(recipe.title)}&body=${encodeURIComponent(
            `${recipe.title}\n\n${recipe.description || ''}\n\nSastojci:\n${
              recipe.ingredients 
                ? recipe.ingredients.split('\n').filter(line => line.trim()).map(line => `• ${line.trim()}`).join('\n')
                : ''
            }\n\nPriprema:\n${recipe.steps || ''}\n\n${window.location.href}`
          )}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <path d="M22 6l-10 7L2 6" />
          </svg>
          <span>Email</span>
        </a>
      </div>

      {shareHint && (
        <p className="share-hint" role="status">
          {shareHint}
        </p>
      )}

    </div>
  );
}
